import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { managedShortcuts } from "@mirage/service-typeahead-search/service/utils/managed-shortcuts";
import * as rx from "rxjs";
import * as op from "rxjs/operators";

import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type { Observable } from "rxjs";
import { twoStageSearch } from "./two-stage-search";
import ResultType = typeahead.ResultType;

const logger = tagged("service-typeahead-search/search/utils");
const metrics = namespace("typeahead-search");

export function performCacheAnalysis(cache: TypeaheadCache) {
  const t0 = performance.now();
  const cacheAnalysis = getCacheAnalysis(cache);
  const t1 = performance.now();
  const cacheAnalysisMs = t1 - t0;
  if (cacheAnalysisMs > 200) {
    const rounded = Math.round(cacheAnalysisMs);
    logger.warn(
      `typeahead cache analysis time is unexpectedly high: ${rounded}ms`,
    );
  }
  metrics.stats("search/cacheAnalysis", t1 - t0, {
    name: "cacheAnalysis",
  });

  return cacheAnalysis;
}

export function getLimits(
  query: string,
  mode: typeahead.Mode,
  limit: number,
  config: typeahead.Config,
): [number, number | undefined] {
  if (query?.startsWith("/")) {
    if (!config.isOmniboxEnabled) {
      return [managedShortcuts.length, undefined];
    }
    return [9, managedShortcuts.length];
  }

  if (!config.isOmniboxEnabled) {
    return [limit, undefined];
  }

  const isNullState = !query.length;
  let totalLimit: number | undefined;
  let perSectionLimit: number | undefined;

  if (isNullState) {
    switch (mode) {
      case typeahead.Mode.Default:
        totalLimit = 15;
        perSectionLimit = 3;
        break;
      case typeahead.Mode.Find:
      case typeahead.Mode.Ask:
      case typeahead.Mode.Write:
        totalLimit = 8;
        perSectionLimit = 4;
        break;
      case typeahead.Mode.Organize:
        totalLimit = 1;
        perSectionLimit = 1;
        break;
    }
  } else {
    switch (mode) {
      case typeahead.Mode.Default:
      case typeahead.Mode.Write:
        totalLimit = 9;
        perSectionLimit = 5;
        break;
      case typeahead.Mode.Find:
      case typeahead.Mode.Ask:
        totalLimit = 9;
        perSectionLimit = undefined;
        break;
      case typeahead.Mode.Organize:
        totalLimit = 1;
        perSectionLimit = 1;
        break;
    }
  }

  // Use mode-specific limits or fallback to defaults
  return [totalLimit ?? 9, perSectionLimit];
}

// In the predicates some sources wish to be included/excluded based on the
// state of the local cache. For example: with an empty query, if the user
// has recent queries we want to the null state to show recent queries, but if
// the user doesn't have recent queries we want the null state to show
// "recommendations".
//
// WARN:
// We should keep the analysis to a minimum! If we need more, we probably need
// a better pattern for a source to be included based on the state of another
// source
export function getCacheAnalysis(
  cache: TypeaheadCache,
): typeahead.CacheAnalysis {
  // <1ms right now. We should implement cache.some() if perf becomes a concern
  const recentQueries = cache.all(CacheKey.RecentQueries) as PreviousQuery[];
  const hasRecentQueries = recentQueries.length > 0;
  return { hasRecentQueries };
}

export function isSuggestedQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.SuggestedQuery;
}

export function isAskUserQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.AskUserQuery;
}

export function isWriteUserQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.WriteUserQuery;
}

export function isRecentQuery(result: typeahead.ScoredResult) {
  return result?.type === ResultType.PreviousQuery;
}

export function isURLShortcut(result: typeahead.ScoredResult) {
  return result?.type === ResultType.URLShortcut;
}

export function sortScoredResults(isScoringSections: boolean) {
  return (
    left: typeahead.ScoredResult,
    right: typeahead.ScoredResult,
  ): number => {
    if (
      !isScoringSections ||
      (left.sectionId === undefined && right.sectionId === undefined)
    ) {
      return right.score - left.score;
    }

    if (left.sectionId === undefined) return -1;
    if (right.sectionId === undefined) return 1;

    if (left.sectionScore !== right.sectionScore) {
      return right.sectionScore - left.sectionScore;
    }

    return right.score - left.score;
  };
}

export function intervals(periods: Array<number>): Array<Observable<void>> {
  return periods.map((period) =>
    rx
      .interval(period)
      .pipe(op.take(1))
      .pipe(op.map(() => undefined)),
  );
}

// These results have a URL parameter we want to dedupe on
export const isURLResult = (result: typeahead.ScoredResult) => {
  return (
    result.type === ResultType.SearchResult ||
    result.type === ResultType.Recommendation ||
    result.type === ResultType.StackItem
  );
};

// Dedupe results based on URL with preference to higher scored results
export const dedupeResultsBasedOnURLAndScore = (
  results: typeahead.ScoredResult[],
): typeahead.ScoredResult[] => {
  const seenResults: { [key: string]: typeahead.ScoredResult } = {};

  results.forEach((result) => {
    if (isURLResult(result) && result.result.url) {
      try {
        const parsedUrl = new URL(result.result.url);
        const cleanUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${parsedUrl.pathname}`;
        if (
          !seenResults[cleanUrl] ||
          seenResults[cleanUrl].score < result.score
        ) {
          seenResults[cleanUrl] = result;
        }
      } catch (e) {
        logger.warn("Invalid url during dedupe");
      }
    } else {
      if (
        !seenResults[result.uuid] ||
        seenResults[result.uuid].score < result.score
      ) {
        seenResults[result.uuid] = result;
      }
    }
  });

  return Object.values(seenResults);
};

/**
 * Clamps the SuggestedQuery (the SERP CTA) to either the first or the second
 * position depending on it's score.
 *
 * In the unlikely case of a tie, the suggested query will be first
 */
export function clampSuggestedQueryMaxPosition(
  userQuery:
    | typeahead.ScoredSuggestedQuery
    | typeahead.ScoredAskUserQuery
    | typeahead.ScoredWriteUserQuery
    | undefined,
  otherResults: typeahead.ScoredResult[],
) {
  if (userQuery == null) {
    // if the suggestedQuery doesn't exist for some reason, just return the otherResults
    return otherResults;
  } else if (userQuery.score <= 0.1) {
    // if the suggestedQuery score is at/below 0.1, it means we should keep the
    // suggestedQuery at the end of the list.
    // this isn't ideal, but since the suggestedQuery score is set by a growthbook flag
    // doing this avoids needing multiple feature flags for this one feature
    // while still allowing experimentation with a control group
    return [...otherResults, userQuery];
  } else if (userQuery.score >= (otherResults[0]?.score ?? 0)) {
    // if the suggestedQuery is the highest scoring result (or it's a tie), then suggested query is first
    return [userQuery, ...otherResults];
  } else {
    // otherwise it's second
    return [...otherResults.slice(0, 1), userQuery, ...otherResults.slice(1)];
  }
}

/**
 *  Recommendation helpers used with multiple sources:
 *  - recommendation
 *  - Draft with source
 */
export function recommendationMatchesWithQuery<T extends Recommendation>(
  query: string,
  recommendations: T[],
  sourceLimit: number,
) {
  // box recommendations into a SearchableResults array
  const searchableResultCandidates = recommendations.map((recommendation) => ({
    searchableStrings: [recommendation.title],
    originalResult: recommendation,
  }));

  // and let twoStageSearch handle the actual searching!
  const matchingSearchableResults = twoStageSearch<T>(
    query,
    searchableResultCandidates,
    {
      limit: sourceLimit,
      isRecents: false,
    },
  );

  // and then we just need to extract the original results from the SearchableResults
  return matchingSearchableResults.map(
    (searchableResult) => searchableResult.originalResult,
  );
}

export function recommendationMatchesWithEmptyQuery<T extends Recommendation>(
  recommendations: T[],
  sourceLimit: number,
): T[] {
  return sortLastClickedMsDesc(recommendations, sourceLimit);
}

export function sortLastClickedMsDesc<T extends Recommendation>(
  recommendations: T[],
  sourceLimit: number,
): T[] {
  const sorted = recommendations.sort(
    (a: T, b: T) => (b.lastClickedMs || 0) - (a.lastClickedMs || 0),
  );
  return sorted.slice(0, sourceLimit);
}
